if (!customElements.get("pin-code-checker")) {
  class PinCodeChecker extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onInput = this.onInput.bind(this);
    }

    connectedCallback() {
      this.input = this.querySelector("[data-pin-input]");
      this.button = this.querySelector("[data-pin-submit]");
      this.buttonLabel = this.querySelector("[data-pin-submit-label]");
      this.status = this.querySelector("[data-pin-status]");
      this.statusText = this.querySelector("[data-pin-status-text]");
      this.statusSuccessIcon = this.querySelector("[data-pin-icon-success]");
      this.statusErrorIcon = this.querySelector("[data-pin-icon-error]");
      this.rows = this.querySelector("[data-pin-rows]");
      this.deliveryText = this.querySelector("[data-pin-delivery-text]");

      this.button?.addEventListener("click", this.onClick);
      this.input?.addEventListener("keydown", this.onKeydown);
      this.input?.addEventListener("input", this.onInput);

      this.restorePin();
    }

    disconnectedCallback() {
      this.button?.removeEventListener("click", this.onClick);
      this.input?.removeEventListener("keydown", this.onKeydown);
      this.input?.removeEventListener("input", this.onInput);
      this.abortController?.abort();
    }

    restorePin() {
      try {
        const saved = window.localStorage.getItem("bolt:pincode");
        if (saved && this.input && !this.input.value) {
          this.input.value = saved;
        }
      } catch (error) {
        // Ignore storage access errors.
      }
    }

    onInput() {
      this.sanitize();
      if (this.dataset.state === "success" || this.dataset.state === "error") {
        this.setIdle();
      }
    }

    onKeydown(event) {
      if (event.key !== "Enter") return;
      event.preventDefault();
      this.onClick();
    }

    onClick() {
      if (this.dataset.state === "loading") return;

      if (this.dataset.state === "success") {
        this.setIdle();
        this.input?.focus();
        this.input?.select();
        return;
      }

      this.checkPin();
    }

    sanitize() {
      if (!this.input) return;
      let value = this.input.value.replace(/\D/g, "").slice(0, 6);
      if (value.startsWith("0")) value = value.replace(/^0+/, "");
      this.input.value = value;
    }

    async checkPin() {
      this.sanitize();
      const pin = this.input?.value || "";

      if (pin.length !== 6) {
        this.setError(this.dataset.emptyMsg || "");
        return;
      }

      this.setLoading();
      this.abortController?.abort();
      this.abortController = new AbortController();

      try {
        const response = await fetch(`https://api.postalpincode.in/pincode/${pin}`, {
          signal: this.abortController.signal,
        });
        const data = await response.json();
        const result = Array.isArray(data) ? data[0] : null;

        if (result && result.Status === "Success" && result.PostOffice) {
          this.setSuccess(pin);
          try {
            window.localStorage.setItem("bolt:pincode", pin);
          } catch (error) {
            // Ignore storage access errors.
          }
        } else {
          this.setError(this.dataset.errorMsg || "");
        }
      } catch (error) {
        if (error?.name === "AbortError") return;
        this.setError(this.dataset.errorMsg || "");
      }
    }

    setLoading() {
      this.dataset.state = "loading";
      this.button?.setAttribute("aria-busy", "true");
      this.button?.setAttribute("disabled", "true");
      if (this.buttonLabel) this.buttonLabel.textContent = this.dataset.checkingText || "";
      this.hideStatus();
      this.toggleRows(false);
    }

    setIdle() {
      this.dataset.state = "idle";
      this.button?.removeAttribute("aria-busy");
      this.button?.removeAttribute("disabled");
      if (this.buttonLabel) this.buttonLabel.textContent = this.dataset.buttonText || "";
      this.hideStatus();
      this.toggleRows(false);
    }

    setSuccess(pin) {
      this.dataset.state = "success";
      this.button?.removeAttribute("aria-busy");
      this.button?.removeAttribute("disabled");
      if (this.buttonLabel) this.buttonLabel.textContent = this.dataset.changeText || "";

      const deliveryMessage = this.buildDeliveryMessage();
      if (this.deliveryText) this.deliveryText.textContent = deliveryMessage;

      const layout = this.dataset.layout;
      if (layout === "compact") {
        this.showStatus("success", deliveryMessage);
      } else if (layout === "details") {
        this.showStatus(
          "success",
          this.replaceTokens(this.dataset.deliveringToText || "", { pin: `<b>${pin}</b>` })
        );
      } else {
        this.hideStatus();
      }

      this.toggleRows(true);
    }

    setError(message) {
      this.dataset.state = "error";
      this.button?.removeAttribute("aria-busy");
      this.button?.removeAttribute("disabled");
      if (this.buttonLabel) this.buttonLabel.textContent = this.dataset.buttonText || "";
      this.showStatus("error", message);
      this.toggleRows(false);
    }

    showStatus(type, message) {
      if (!this.status) return;
      this.status.hidden = false;
      this.status.dataset.statusType = type;
      if (this.statusText) this.statusText.innerHTML = message;
      if (this.statusSuccessIcon) this.statusSuccessIcon.hidden = type !== "success";
      if (this.statusErrorIcon) this.statusErrorIcon.hidden = type !== "error";
    }

    hideStatus() {
      if (!this.status) return;
      this.status.hidden = true;
      this.status.removeAttribute("data-status-type");
      if (this.statusText) this.statusText.textContent = "";
      if (this.statusSuccessIcon) this.statusSuccessIcon.hidden = true;
      if (this.statusErrorIcon) this.statusErrorIcon.hidden = true;
    }

    toggleRows(show) {
      if (!this.rows) return;
      this.rows.hidden = !show;
    }

    buildDeliveryMessage() {
      const startDays = Number(this.dataset.startDays || 0);
      const endDays = Number(this.dataset.endDays || startDays);
      const minutes = Number(this.dataset.addMinutes || 0);
      const start = this.formatDate(startDays);
      const end = this.formatDate(endDays);
      const tokens = { date: start, start, end, time: this.formatTime(minutes) };

      let message =
        startDays === endDays
          ? this.replaceTokens(this.dataset.deliveredByText || "", tokens)
          : this.replaceTokens(this.dataset.deliveryRangeText || "", tokens);

      if (minutes > 0 && this.dataset.orderedBeforeText) {
        message = `${message} ${this.replaceTokens(this.dataset.orderedBeforeText, tokens)}`;
      }

      return message;
    }

    formatDate(days) {
      const date = new Date();
      date.setHours(12, 0, 0, 0);
      date.setDate(date.getDate() + Number(days || 0));
      return date.toLocaleDateString(this.dataset.locale || undefined, {
        weekday: this.dataset.weekdayLength || "short",
        day: "numeric",
        month: this.dataset.monthLength || "short",
      });
    }

    formatTime(minutes) {
      const date = new Date(Date.now() + Number(minutes || 0) * 60000);
      return date.toLocaleTimeString(this.dataset.locale || undefined, {
        hour: "numeric",
        minute: "2-digit",
      });
    }

    replaceTokens(template, tokens) {
      return Object.keys(tokens).reduce((result, key) => {
        return result.split(`[${key}]`).join(tokens[key]);
      }, template);
    }
  }

  customElements.define("pin-code-checker", PinCodeChecker);
}
